import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { HelpCircle, X } from "lucide-react";

interface HelpIconProps {
  text: string;
}

export function HelpIcon({ text }: HelpIconProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLSpanElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const hoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPos({ top: rect.bottom + 6, left: rect.left + rect.width / 2 });
  }, []);

  const openPopup = useCallback(() => {
    updatePosition();
    setOpen(true);
  }, [updatePosition]);

  const closePopup = useCallback(() => {
    setOpen(false);
  }, []);

  const handleMouseEnter = () => {
    hoverTimeout.current = setTimeout(openPopup, 300);
  };

  const handleMouseLeave = () => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (open) {
      closePopup();
    } else {
      openPopup();
    }
  };

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node) &&
          triggerRef.current && !triggerRef.current.contains(e.target as Node)) {
        closePopup();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, closePopup]);

  useEffect(() => {
    if (!open) return;
    const handler = () => updatePosition();
    window.addEventListener("scroll", handler, true);
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("scroll", handler, true);
      window.removeEventListener("resize", handler);
    };
  }, [open, updatePosition]);

  return (
    <>
      <span
        ref={triggerRef}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="inline-flex items-center cursor-help ml-1 align-middle"
        role="button"
        tabIndex={0}
        aria-label="Help"
      >
        <HelpCircle size={13} className="text-muted-foreground hover:text-foreground transition-colors" />
      </span>

      {open &&
        createPortal(
          <div
            ref={popupRef}
            style={{ top: pos.top, left: pos.left }}
            className="fixed z-[9999] w-80 -translate-x-1/2 bg-popover border border-border rounded-lg shadow-xl p-3 text-xs text-popover-foreground leading-relaxed"
          >
            <div className="flex items-start gap-2">
              <span className="flex-1">{text}</span>
              <button
                onClick={closePopup}
                className="shrink-0 text-muted-foreground hover:text-foreground"
              >
                <X size={12} />
              </button>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}